import React, { useState, useEffect } from "react";
import { signInWithEmailAndPassword, onAuthStateChanged, signOut, createUserWithEmailAndPassword } from "firebase/auth";
import { collection, addDoc, query, onSnapshot, orderBy, deleteDoc, doc, updateDoc, setDoc, runTransaction } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { initializeApp, deleteApp, getApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import ImageUploader from './ImageUploader';
import BlogManager from './BlogManager';

const AdminPage = ({ db, auth, appId, onLogout }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loginData, setLoginData] = useState({ email: "", password: "" });
    const [loginError, setLoginError] = useState("");
    const [activeTab, setActiveTab] = useState('dashboard');
    
    // User management
    const [users, setUsers] = useState([]);
    const [newUserEmail, setNewUserEmail] = useState('');
    const [generatedPassword, setGeneratedPassword] = useState('');
    const [createdUserEmail, setCreatedUserEmail] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    
    // Blog management
    const [blogTitle, setBlogTitle] = useState('');
    const [blogSummary, setBlogSummary] = useState('');
    const [blogContent, setBlogContent] = useState('');
    const [blogImageFile, setBlogImageFile] = useState(null);
    const [blogDate, setBlogDate] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    
    // Other data
    const [testimonials, setTestimonials] = useState([]);
    const [inquiries, setInquiries] = useState([]);
    const [reservations, setReservations] = useState([]);
    
    // Lections management
    const [lections, setLections] = useState([]);
    const [archivedLections, setArchivedLections] = useState([]);
    const [showArchivedLections, setShowArchivedLections] = useState(false);
    const [lectionName, setLectionName] = useState('');
    const [lectionDescription, setLectionDescription] = useState('');
    const [lectionDate, setLectionDate] = useState('');
    const [lectionTime, setLectionTime] = useState('');
    const [lectionImageFile, setLectionImageFile] = useState(null);
    const [isLectionUploading, setIsLectionUploading] = useState(false);

    // Stories management
    const [stories, setStories] = useState([]);
    const [storyTitle, setStoryTitle] = useState('');
    const [storyAuthor, setStoryAuthor] = useState('');
    const [storyContent, setStoryContent] = useState('');
    const [storyExcerpt, setStoryExcerpt] = useState('');
    const [storyMood, setStoryMood] = useState('🌿');
    const [storyCategory, setStoryCategory] = useState('');
    const [storyNote, setStoryNote] = useState('');
    const [storyImageFile, setStoryImageFile] = useState(null);
    const [isStoryUploading, setIsStoryUploading] = useState(false);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setUser(user && !user.isAnonymous && user.email === 'cestamehovnitrnihoja@seznam.cz' ? user : null);
            setLoading(false);
        });
        return () => unsubscribe();
    }, [auth]);

    // Load data when user is authenticated
    useEffect(() => {
        if (user) {
            // Load users
            const usersQuery = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
            const unsubscribeUsers = onSnapshot(usersQuery, (querySnapshot) => {
                const usersList = [];
                querySnapshot.forEach((doc) => {
                    usersList.push({ id: doc.id, ...doc.data() });
                });
                setUsers(usersList);
            });

            // Load testimonials
            const testimonialsQuery = query(collection(db, 'testimonials'));
            const unsubscribeTestimonials = onSnapshot(testimonialsQuery, (querySnapshot) => {
                const testimonialsList = [];
                querySnapshot.forEach((doc) => {
                    testimonialsList.push({ id: doc.id, ...doc.data() });
                });
                setTestimonials(testimonialsList);
            });

            // Load inquiries
            const inquiriesQuery = query(collection(db, 'contactMessages'));
            const unsubscribeInquiries = onSnapshot(inquiriesQuery, (querySnapshot) => {
                const inquiriesList = [];
                querySnapshot.forEach((doc) => {
                    inquiriesList.push({ id: doc.id, ...doc.data() });
                });
                setInquiries(inquiriesList);
            });

            // Load reservations
            const reservationsQuery = query(collection(db, 'reservations'), orderBy('createdAt', 'desc'));
            const unsubscribeReservations = onSnapshot(reservationsQuery, (querySnapshot) => {
                const reservationsList = [];
                querySnapshot.forEach((doc) => {
                    reservationsList.push({ id: doc.id, ...doc.data() });
                });
                setReservations(reservationsList);
            });

            // Load lections with enhanced debugging
            const lectionsQuery = query(collection(db, 'lections'), orderBy('date', 'asc'));
            const unsubscribeLections = onSnapshot(lectionsQuery, async (querySnapshot) => {
                const lectionsList = [];
                const archivedList = [];
                const timestamp = new Date().toISOString();
                const now = new Date();
                console.log(`🔄 [${timestamp}] onSnapshot triggered, processing ${querySnapshot.docs.length} lections`);
                
                querySnapshot.forEach((docSnapshot) => {
                    const lectionData = { id: docSnapshot.id, ...docSnapshot.data() };
                    const attendeesCount = lectionData.attendees?.length || 0;
                    
                    // Check if lection should be archived (24 hours after date + time)
                    const lectionDateTime = new Date(`${lectionData.date}T${lectionData.time || '00:00'}`);
                    const hoursAfterLection = (now - lectionDateTime) / (1000 * 60 * 60);
                    const shouldBeArchived = hoursAfterLection >= 24;
                    
                    console.log(`📋 [${timestamp}] Lection ${lectionData.id}:`);
                    console.log(`  - Attendees count: ${attendeesCount}`);
                    console.log(`  - Last operation: ${lectionData.lastOperation || 'none'}`);
                    console.log(`  - Hours after lection: ${hoursAfterLection.toFixed(2)}`);
                    console.log(`  - Should be archived: ${shouldBeArchived}`);
                    
                    if (lectionData.attendees && lectionData.attendees.length > 0) {
                        const attendeeNames = lectionData.attendees.map((a, index) => {
                            if (typeof a === 'string') {
                                return `${index + 1}. ${a} (string)`;
                            }
                            return `${index + 1}. ${a.name || a.email || 'unknown'} (${a.type || 'user'})`;
                        });
                        console.log(`  - Attendees list:`, attendeeNames);
                    }
                    
                    // Auto-archive if needed
                    if (shouldBeArchived && !lectionData.archived) {
                        // Mark as archived in database
                        updateDoc(doc(db, 'lections', lectionData.id), {
                            archived: true,
                            archivedAt: new Date()
                        }).catch(err => console.error('Error archiving lection:', err));
                        
                        lectionData.archived = true;
                    }
                    
                    // Separate into active and archived
                    if (lectionData.archived) {
                        archivedList.push(lectionData);
                    } else {
                        lectionsList.push(lectionData);
                    }
                });
                
                console.log(`🔄 [${timestamp}] Setting lections state with ${lectionsList.length} active and ${archivedList.length} archived lections`);
                setLections(lectionsList);
                setArchivedLections(archivedList);
                
                // Log total attendees across all lections
                const totalAttendees = lectionsList.reduce((sum, lection) => sum + (lection.attendees?.length || 0), 0);
                console.log(`📊 [${timestamp}] Total attendees across active lections: ${totalAttendees}`);
            }, (error) => {
                console.error('❌ onSnapshot error:', error);
            });

            // Load stories
            const storiesQuery = query(collection(db, appId ? `/artifacts/${appId}/public/data/stories` : 'stories'), orderBy('createdAt', 'desc'));
            const unsubscribeStories = onSnapshot(storiesQuery, (querySnapshot) => {
                const storiesList = [];
                querySnapshot.forEach((docSnapshot) => {
                    storiesList.push({ id: docSnapshot.id, ...docSnapshot.data() });
                });
                setStories(storiesList);
                console.log(`📚 Loaded ${storiesList.length} stories`);
            }, (error) => {
                console.error('❌ Stories onSnapshot error:', error);
            });

            return () => {
                unsubscribeUsers();
                unsubscribeTestimonials();
                unsubscribeInquiries();
                unsubscribeReservations();
                unsubscribeLections();
                unsubscribeStories();
            };
        }
    }, [user, db, appId]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoginError("");
        
        // if (loginData.email.trim().toLowerCase() !== "cestamehovnitrnihoja@seznam.cz") {
        if (loginData.email.trim().toLowerCase() !== "cestamehovnitrnihoja@seznam.cz") {
            setLoginError("Nepovolený přístup");
            return;
        }
        
        try {
            await signInWithEmailAndPassword(auth, loginData.email.trim(), loginData.password);
        } catch (error) {
            setLoginError("Neplatné přihlašovací údaje");
        }
    };

    const handleLogout = async () => {
        try {
            await signOut(auth);
            onLogout();
        } catch (error) {
            console.error("Error signing out:", error);
        }
    };

    const generatePassword = () => {
        return Array.from(crypto.getRandomValues(new Uint8Array(18)), n => n.toString(16).padStart(2, '0')).join('');
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        if (!newUserEmail.trim()) {
            alert('Zadejte email klienta');
            return;
        }

        try {
            const password = generatePassword();
            
            // Create user in Firebase Auth
            // A secondary auth instance keeps the administrator signed in while creating a client.
            const secondaryApp = initializeApp(getApp().options, `client-creation-${Date.now()}`);
            let userCredential;
            try {
                userCredential = await createUserWithEmailAndPassword(getAuth(secondaryApp), newUserEmail.trim(), password);
            } finally {
                await deleteApp(secondaryApp);
            }
            
            // Save user data in Firestore
            await setDoc(doc(db, 'users', userCredential.user.uid), {
                email: newUserEmail,
                createdAt: new Date(),
                createdBy: user.email,
                needsPasswordChange: true,
                role: 'client'
            });

            setGeneratedPassword(password);
            setCreatedUserEmail(newUserEmail);
            setShowPassword(true);
            setNewUserEmail('');
            

            
        } catch (error) {
            console.error('Error creating user:', error);
            alert('Chyba při vytváření uživatele: ' + error.message);
        }
    };

    const handleAddPost = async (e) => {
        e.preventDefault();
        if (!blogTitle || !blogSummary || !blogContent || !blogImageFile || !blogDate) {
            alert('Vyplňte všechna pole');
            return;
        }
        setIsUploading(true);

        try {
            console.log('Starting blog post upload...');
            console.log('appId:', appId);
            
            // Upload image
            const storage = getStorage();
            const timestamp = Date.now();
            const fileName = `${timestamp}_${blogImageFile.name}`;
            const storageRef = ref(storage, `blog-images/${fileName}`);
            
            console.log('Uploading image to storage...');
            await uploadBytes(storageRef, blogImageFile);
            console.log('Image uploaded, getting download URL...');
            const imageUrl = await getDownloadURL(storageRef);
            console.log('Image URL obtained:', imageUrl);

            // Save blog post - try simple collection path first
            const collectionPath = appId ? `/artifacts/${appId}/public/data/blogPosts` : 'blogPosts';
            console.log('Saving to collection:', collectionPath);
            
            await addDoc(collection(db, collectionPath), {
                title: blogTitle,
                summary: blogSummary,
                content: blogContent,
                image: imageUrl,
                date: blogDate,
                createdAt: new Date(),
                author: user.email
            });
            
            console.log('Blog post saved successfully');

            // Reset form
            setBlogTitle('');
            setBlogSummary('');
            setBlogContent('');
            setBlogImageFile(null);
            setBlogDate('');
            alert('Příspěvek byl úspěšně přidán');
        } catch (error) {
            console.error('Error adding post:', error);
            console.error('Error details:', error.message);
            alert('Chyba při přidávání příspěvku: ' + error.message);
        } finally {
            setIsUploading(false);
        }
    };

    const handleAddStory = async (e) => {
        e.preventDefault();
        if (!storyTitle || !storyContent) {
            alert('Vyplňte alespoň název a obsah příběhu');
            return;
        }
        setIsStoryUploading(true);

        try {
            console.log('Starting story upload...');
            console.log('appId:', appId);
            
            let imageUrl = null;
            
            // Upload image if provided
            if (storyImageFile) {
                const storage = getStorage();
                const timestamp = Date.now();
                const fileName = `${timestamp}_${storyImageFile.name}`;
                const storageRef = ref(storage, `story-images/${fileName}`);
                
                console.log('Uploading image to storage...');
                await uploadBytes(storageRef, storyImageFile);
                console.log('Image uploaded, getting download URL...');
                imageUrl = await getDownloadURL(storageRef);
                console.log('Image URL obtained:', imageUrl);
            }
            
            const collectionPath = appId ? `/artifacts/${appId}/public/data/stories` : 'stories';
            console.log('Saving story to collection:', collectionPath);
            
            await addDoc(collection(db, collectionPath), {
                title: storyTitle,
                author: storyAuthor || 'Neznámý autor',
                content: storyContent,
                excerpt: storyExcerpt || storyContent.substring(0, 150) + '...',
                mood: storyMood || '🌿',
                category: storyCategory || '',
                note: storyNote || '',
                image: imageUrl,
                date: new Date().toLocaleDateString('cs-CZ'),
                createdAt: new Date(),
                viewCount: 0
            });
            
            console.log('Story saved successfully');

            // Reset form
            setStoryTitle('');
            setStoryAuthor('');
            setStoryContent('');
            setStoryExcerpt('');
            setStoryMood('🌿');
            setStoryCategory('');
            setStoryNote('');
            setStoryImageFile(null);
            alert('Příběh byl úspěšně přidán do zahrady');
        } catch (error) {
            console.error('Error adding story:', error);
            alert('Chyba při přidávání příběhu: ' + error.message);
        } finally {
            setIsStoryUploading(false);
        }
    };

    const handleDeleteStory = async (storyId) => {
        if (!window.confirm('Opravdu chcete smazat tento příběh?')) {
            return;
        }

        try {
            const collectionPath = appId ? `/artifacts/${appId}/public/data/stories` : 'stories';
            await deleteDoc(doc(db, collectionPath, storyId));
            alert('Příběh byl úspěšně smazán');
        } catch (error) {
            console.error('Error deleting story:', error);
            alert('Chyba při mazání příběhu: ' + error.message);
        }
    };

    const handleAddLection = async (e) => {
        e.preventDefault();
        if (!lectionName || !lectionDescription || !lectionDate || !lectionTime) {
            alert('Vyplňte všechna povinná pole (název, popis, datum, čas)');
            return;
        }
        setIsLectionUploading(true);

        try {
            let imageUrl = '';
            
            // Upload image only if provided, otherwise use default
            if (lectionImageFile) {
                try {
                    console.log('Uploading image:', lectionImageFile.name);
                    const storage = getStorage();
                    const storageRef = ref(storage, `lection-images/${lectionImageFile.name}`);
                    await uploadBytes(storageRef, lectionImageFile);
                    imageUrl = await getDownloadURL(storageRef);
                    console.log('Image uploaded successfully:', imageUrl);
                } catch (storageError) {
                    console.warn('Image upload failed, using default image:', storageError.message);
                    // Fall back to default image if upload fails
                    imageUrl = 'https://firebasestorage.googleapis.com/v0/b/cchi-kung.firebasestorage.app/o/blog-images%2F1760284976057_WhatsApp%20Image%202025-08-24%20at%2009.53.02.jpeg?alt=media&token=53bbd929-05f2-4c64-a51e-1e2b34f0afec';
                }
            } else {
                // Use default image from the recurring lections
                imageUrl = 'https://firebasestorage.googleapis.com/v0/b/cchi-kung.firebasestorage.app/o/blog-images%2F1760284976057_WhatsApp%20Image%202025-08-24%20at%2009.53.02.jpeg?alt=media&token=53bbd929-05f2-4c64-a51e-1e2b34f0afec';
                console.log('Using default image:', imageUrl);
            }

            // Save lection
            await addDoc(collection(db, 'lections'), {
                name: lectionName,
                description: lectionDescription,
                date: lectionDate,
                time: lectionTime,
                image: imageUrl,
                createdAt: new Date(),
                createdBy: user.email,
                attendees: []
            });

            // Reset form
            setLectionName('');
            setLectionDescription('');
            setLectionDate('');
            setLectionTime('');
            setLectionImageFile(null);
            alert('Lekce byla úspěšně vytvořena');
        } catch (error) {
            console.error('Error adding lection:', error);
            console.error('Error details:', {
                name: error.name,
                message: error.message,
                code: error.code,
                stack: error.stack
            });
            console.error('Form values at time of error:', {
                lectionName,
                lectionDescription,
                lectionDate,
                lectionTime,
                lectionImageFile: lectionImageFile ? {
                    name: lectionImageFile.name,
                    size: lectionImageFile.size,
                    type: lectionImageFile.type
                } : null,
                userEmail: user?.email
            });
            alert(`Chyba při vytváření lekce: ${error.message}`);
        } finally {
            setIsLectionUploading(false);
        }
    };

    const deleteLection = async (lectionId) => {
        if (window.confirm('Opravdu chcete smazat tuto lekci?')) {
            try {
                await deleteDoc(doc(db, 'lections', lectionId));
            } catch (error) {
                console.error('Error deleting lection:', error);
                alert('Chyba při mazání lekce');
            }
        }
    };

    // Funkce pro přidání účastníka do lekce
    const addAttendeeToLection = async (lectionId, userEmail) => {
        console.log('🚨 addAttendeeToLection called with:', {lectionId, userEmail});
        try {
            const lection = lections.find(l => l.id === lectionId);
            if (!lection) {
                alert('Lekce nenalezena');
                return;
            }

            // Kontrola limitu 12 lidí
            if (lection.attendees && lection.attendees.length >= 12) {
                alert('Lekce je plná! Maximum je 12 účastníků.');
                return;
            }

            // Kontrola, zda už není přihlášen (podporuje jak stringy tak objekty)
            if (lection.attendees) {
                const isAlreadyAttending = lection.attendees.some(attendee => {
                    if (typeof attendee === 'string') {
                        return attendee === userEmail;
                    }
                    return attendee.email === userEmail;
                });
                
                if (isAlreadyAttending) {
                    alert('Tento účastník je již přihlášen na lekci');
                    return;
                }
            }

            // Přidáme jako objekt s dodatečnými informacemi
            const newAttendee = {
                email: userEmail,
                joinedAt: new Date(),
                userId: userEmail // Můžeme později použít skutečné userId
            };

            const updatedAttendees = [...(lection.attendees || []), newAttendee];
            await updateDoc(doc(db, 'lections', lectionId), {
                attendees: updatedAttendees,
                updatedAt: new Date()
            });

            alert('Účastník byl úspěšně přidán do lekce');
        } catch (error) {
            console.error('Error adding attendee:', error);
            alert('Chyba při přidávání účastníka');
        }
    };

    // Funkce pro přidání účastníka s permanentkou
    const addAttendeeWithPass = async (lectionId, userEmail) => {
        try {
            const user = users.find(u => u.email === userEmail);
            if (!user) {
                alert('Uživatel nenalezen');
                return;
            }

            // Kontrola permanentky
            if (!user.remainingLections || user.remainingLections <= 0) {
                alert('Uživatel nemá zbývající lekce na permanentce');
                return;
            }

            // Přidej do lekce
            await addAttendeeToLection(lectionId, userEmail);

            // Odeber jednu lekci z permanentky
            await updateDoc(doc(db, 'users', user.id), {
                remainingLections: user.remainingLections - 1,
                updatedAt: new Date()
            });

            alert(`Účastník přidán a odečtena 1 lekce z permanentky. Zbývá: ${user.remainingLections - 1} lekcí`);
        } catch (error) {
            console.error('Error adding attendee with pass:', error);
            alert('Chyba při přidávání účastníka s permanentkou');
        }
    };

    // Funkce pro přidání účastníka bez účtu (jen jméno) - NEW MAP-BASED VERSION
    const addAttendeeByName = async (lectionId, participantName) => {
        const timestamp = Date.now();
        const guestId = `guest_${timestamp}_${Math.random().toString(36).substr(2, 9)}`;
        console.log('🚀 Starting addAttendeeByName MAP-BASED VERSION with:', {lectionId, participantName, guestId});
        
        if (!participantName || participantName.trim() === '') {
            console.error('❌ Invalid participant name');
            alert('Zadejte platné jméno účastníka');
            return;
        }

        const trimmedName = participantName.trim();
        
        try {
            const docRef = doc(db, 'lections', lectionId);
            console.log('📄 Document reference:', docRef.path);
            
            // Use transaction for atomic update with fresh data
            const result = await runTransaction(db, async (transaction) => {
                console.log('🔒 Starting transaction...');
                const docSnapshot = await transaction.get(docRef);
                
                if (!docSnapshot.exists()) {
                    throw new Error('Lection does not exist');
                }
                
                const currentData = docSnapshot.data();
                
                // NEW: Use attendeesMap instead of attendees array
                const currentAttendeesMap = currentData.attendeesMap || {};
                const attendeesList = Object.values(currentAttendeesMap);
                
                console.log('🔒 Transaction: Fresh data from DB - attendees count:', attendeesList.length);
                console.log('🔒 Transaction: Current attendees map:', currentAttendeesMap);
                
                // Check capacity limit (changed to 12 as per your admin panel)
                if (attendeesList.length >= 12) {
                    throw new Error('Lekce je plná! Maximum je 12 účastníků.');
                }
                
                // Check for duplicates by name (case insensitive)
                const isDuplicate = attendeesList.some(attendee => {
                    if (attendee.name && attendee.name.toLowerCase() === trimmedName.toLowerCase()) {
                        console.log('🔍 Duplicate found:', attendee.name, 'vs', trimmedName);
                        return true;
                    }
                    if (attendee.email && attendee.email.toLowerCase() === trimmedName.toLowerCase()) {
                        console.log('🔍 Duplicate found by email:', attendee.email, 'vs', trimmedName);
                        return true;
                    }
                    return false;
                });
                
                if (isDuplicate) {
                    throw new Error('Účastník s tímto jménem je již přihlášen na lekci');
                }
                
                // Create new guest attendee with unique ID
                const newGuestAttendee = {
                    id: guestId,
                    name: trimmedName,
                    type: 'guest',
                    joinedAt: new Date(),
                    addedBy: user.email,
                    timestamp: timestamp
                };
                
                // Create updated attendees map
                const updatedAttendeesMap = {
                    ...currentAttendeesMap,
                    [guestId]: newGuestAttendee
                };
                
                console.log('🔒 Transaction: Adding guest attendee:', newGuestAttendee);
                console.log('🔒 Transaction: New attendees count:', Object.keys(updatedAttendeesMap).length);
                console.log('🔒 Transaction: Writing to database...');
                
                // Update the document with new map structure
                transaction.update(docRef, {
                    attendeesMap: updatedAttendeesMap,
                    // Keep legacy attendees array for backward compatibility (convert map to array)
                    attendees: Object.values(updatedAttendeesMap),
                    updatedAt: new Date(),
                    lastOperation: guestId
                });
                
                console.log('� Transaction: Update queued successfully');
                return { success: true, newCount: Object.keys(updatedAttendeesMap).length, attendee: newGuestAttendee };
            });
            
            console.log('✅ Transaction completed successfully:', result);
            
            // Success message
            alert(`Host "${trimmedName}" byl úspěšně přidán do lekce`);
            console.log(`✅ SUCCESS: Host "${trimmedName}" byl úspěšně přidán do lekce s ID: ${guestId}`);
            
        } catch (error) {
            console.error('❌ Error adding guest attendee:', error);
            
            // User-friendly error messages
            let errorMessage = 'Chyba při přidávání účastníka';
            if (error.message.includes('plná')) {
                errorMessage = 'Lekce je plná! Maximum je 12 účastníků.';
            } else if (error.message.includes('již přihlášen')) {
                errorMessage = 'Účastník s tímto jménem je již přihlášen na lekci.';
            } else if (error.message.includes('does not exist')) {
                errorMessage = 'Lekce nenalezena.';
            }
            
            alert(errorMessage);
        }
    };

    // Funkce pro odebrání účastníka z lekce - MAP-BASED VERSION
    const removeAttendee = async (lectionId, attendeeIdentifier) => {
        console.log('Starting removeAttendee MAP-BASED with:', {lectionId, attendeeIdentifier});
        
        try {
            const docRef = doc(db, 'lections', lectionId);

            // Use transaction for atomic update
            await runTransaction(db, async (transaction) => {
                const docSnapshot = await transaction.get(docRef);
                if (!docSnapshot.exists()) {
                    throw new Error('Lection does not exist');
                }
                
                const currentData = docSnapshot.data();
                const currentAttendeesMap = currentData.attendeesMap || {};
                const attendeesList = Object.values(currentAttendeesMap);
                
                console.log('🔒 Transaction: Current attendees count:', attendeesList.length);
                console.log('🔒 Transaction: Trying to remove:', attendeeIdentifier);
                
                // Find and remove the attendee from map
                const updatedAttendeesMap = { ...currentAttendeesMap };
                let attendeeRemoved = false;
                
                // Try to remove by ID first (for guests with IDs)
                if (updatedAttendeesMap[attendeeIdentifier]) {
                    console.log('🔍 Removing by ID:', attendeeIdentifier);
                    delete updatedAttendeesMap[attendeeIdentifier];
                    attendeeRemoved = true;
                } else {
                    // Try to remove by name or email
                    for (const [id, attendee] of Object.entries(updatedAttendeesMap)) {
                        if (attendee.name === attendeeIdentifier || attendee.email === attendeeIdentifier) {
                            console.log('🔍 Removing by name/email:', attendee.name || attendee.email, 'with ID:', id);
                            delete updatedAttendeesMap[id];
                            attendeeRemoved = true;
                            break;
                        }
                    }
                }
                
                if (!attendeeRemoved) {
                    throw new Error('Účastník nenalezen v seznamu');
                }
                
                console.log('🔒 Transaction: Updated attendees count:', Object.keys(updatedAttendeesMap).length);
                
                transaction.update(docRef, {
                    attendeesMap: updatedAttendeesMap,
                    // Keep legacy attendees array for backward compatibility
                    attendees: Object.values(updatedAttendeesMap),
                    updatedAt: new Date()
                });
            });
            
            console.log('✅ Remove transaction completed successfully');
            console.log(`✅ SUCCESS: Účastník "${attendeeIdentifier}" byl úspěšně odebrán z lekce`);
            
        } catch (error) {
            console.error('❌ Error removing attendee:', error);
            alert(`Chyba při odebírání účastníka: ${error.message}`);
        }
    };

    // Funkce pro automatické vytváření opakujících se lekcí
    const createRecurringLections = async () => {
        try {
            const today = new Date();
            const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, today.getDate());
            
            // Default image URL for recurring lections
            const defaultLectionImage = 'https://firebasestorage.googleapis.com/v0/b/cchi-kung.firebasestorage.app/o/blog-images%2F1760284976057_WhatsApp%20Image%202025-08-24%20at%2009.53.02.jpeg?alt=media&token=53bbd929-05f2-4c64-a51e-1e2b34f0afec';
            
            // Vytvoř pondělní lekce (60 minut)
            const mondayLections = [];
            for (let date = new Date(today); date <= nextMonth; date.setDate(date.getDate() + 1)) {
                if (date.getDay() === 1) { // Pondělí
                    mondayLections.push({
                        name: 'Harmonizační sestava - Cesta mého vnitřního já',
                        description: '90-minutová harmonizující sestava s tibetskými mísami',
                        date: date.toISOString().split('T')[0],
                        time: '18:00',
                        duration: 90,
                        image: defaultLectionImage,
                        createdAt: new Date(),
                        createdBy: user.email,
                        attendees: [],
                        maxCapacity: 12,
                        recurring: true,
                        dayOfWeek: 1
                    });
                }
            }

            // Vytvoř čtvrteční lekce (90 minut)
            const thursdayLections = [];
            for (let date = new Date(today); date <= nextMonth; date.setDate(date.getDate() + 1)) {
                if (date.getDay() === 3) { // Středa
                    thursdayLections.push({
                        name: 'Pohyb s vějířem - propojení dechu a pohybu',
                        description: '90-minutová harmonizující sestava s tibetskými mísami',
                        date: date.toISOString().split('T')[0],
                        time: '18:00',
                        duration: 90,
                        image: defaultLectionImage,
                        createdAt: new Date(),
                        createdBy: user.email,
                        attendees: [],
                        maxCapacity: 12,
                        recurring: true,
                        dayOfWeek: 4
                    });
                }
            }

            // Ulož do databáze
            const allLections = [...mondayLections, ...thursdayLections];
            for (const lection of allLections) {
                await addDoc(collection(db, 'lections'), lection);
            }

            alert(`Vytvořeno ${allLections.length} opakujících se lekcí na další měsíc`);
        } catch (error) {
            console.error('Error creating recurring lections:', error);
            alert('Chyba při vytváření opakujících se lekcí');
        }
    };

    const deleteUser = async (userId) => {
        if (window.confirm('Opravdu chcete smazat tohoto uživatele?')) {
            try {
                await deleteDoc(doc(db, 'users', userId));
            } catch (error) {
                console.error('Error deleting user:', error);
                alert('Chyba při mazání uživatele');
            }
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('cs-CZ') + ' ' + date.toLocaleTimeString('cs-CZ', { 
            hour: '2-digit', 
            minute: '2-digit' 
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-white text-xl">Načítání...</div>
            </div>
        );
    }

    if (!user || user.email !== "cestamehovnitrnihoja@seznam.cz") {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="bg-gray-800 p-8 rounded-xl shadow-2xl w-full max-w-md">
                    <div className="text-center mb-8">
                        <h1 className="text-3xl font-bold text-white mb-2">Prostor pro lektora</h1>
                        <p className="text-gray-400">Přihlášení pro lektora</p>
                    </div>
                    <form onSubmit={handleLogin} className="space-y-6">
                        <div>
                            <label htmlFor="admin-email" className="block text-white text-sm font-medium mb-2">E-mail</label>
                            <input
                                type="email" id="admin-email" autoComplete="username"
                                value={loginData.email}
                                onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500 transition duration-300"
                                placeholder="cestamehovnitrnihoja@seznam.cz"
                                required
                            />
                        </div>
                        <div>
                            <label htmlFor="admin-password" className="block text-white text-sm font-medium mb-2">Heslo</label>
                            <input
                                type="password" id="admin-password" autoComplete="current-password"
                                value={loginData.password}
                                onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500 transition duration-300"
                                required
                            />
                        </div>
                        {loginError && (
                            <div role="alert" className="text-red-400 text-sm text-center">{loginError}</div>
                        )}
                        <button
                            type="submit"
                            className="w-full bg-yellow-600 hover:bg-yellow-700 text-black font-bold py-3 px-4 rounded-lg transition duration-300"
                        >
                            Přihlásit se
                        </button>
                    </form>
                    <div className="mt-6 text-center">
                        <a href="/" className="text-yellow-500 hover:text-yellow-400 text-sm transition duration-300">
                            ← Zpět na hlavní stránku
                        </a>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900 text-white">
            <header className="bg-gray-800 shadow-lg">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center py-4">
                        <div className="flex flex-wrap items-center gap-2 text-center sm:text-left">
                            <h1 className="text-2xl font-bold text-yellow-500">Lektorský Panel</h1>
                            <span className="text-gray-400 break-all sm:break-normal">Přihlášen jako: {user.email}</span>
                        </div>
                        <div className="flex flex-wrap items-center gap-3 sm:gap-4 sm:flex-nowrap sm:justify-end">
                            <a href="/" className="text-gray-300 hover:text-white transition duration-300">
                                Hlavní stránka
                            </a>
                            <button
                                onClick={handleLogout}
                                className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg transition duration-300"
                            >
                                Odhlásit se
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            <nav className="bg-gray-800 border-t border-gray-700">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex w-full overflow-x-auto space-x-4 sm:space-x-8 py-2">
                        <button
                            onClick={() => setActiveTab('dashboard')}
                            className={`py-4 px-1 border-b-2 font-medium text-sm transition duration-300 whitespace-nowrap shrink-0 ${
                                activeTab === 'dashboard' 
                                    ? 'border-yellow-500 text-yellow-500' 
                                    : 'border-transparent text-gray-400 hover:text-gray-300'
                            }`}
                        >
                            Dashboard
                        </button>
                        <button
                            onClick={() => setActiveTab('lections')}
                            className={`py-4 px-1 border-b-2 font-medium text-sm transition duration-300 whitespace-nowrap shrink-0 ${
                                activeTab === 'lections' 
                                    ? 'border-yellow-500 text-yellow-500' 
                                    : 'border-transparent text-gray-400 hover:text-gray-300'
                            }`}
                        >
                            Správa lekcí ({lections.length})
                        </button>
                        <button
                            onClick={() => setActiveTab('users')}
                            className={`py-4 px-1 border-b-2 font-medium text-sm transition duration-300 whitespace-nowrap shrink-0 ${
                                activeTab === 'users' 
                                    ? 'border-yellow-500 text-yellow-500' 
                                    : 'border-transparent text-gray-400 hover:text-gray-300'
                            }`}
                        >
                            Správa uživatelů ({users.length})
                        </button>
                        <button
                            onClick={() => setActiveTab('blog')}
                            className={`py-4 px-1 border-b-2 font-medium text-sm transition duration-300 whitespace-nowrap shrink-0 ${
                                activeTab === 'blog' 
                                    ? 'border-yellow-500 text-yellow-500' 
                                    : 'border-transparent text-gray-400 hover:text-gray-300'
                            }`}
                        >
                            Blog příspěvky
                        </button>
                        <button
                            onClick={() => setActiveTab('stories')}
                            className={`py-4 px-1 border-b-2 font-medium text-sm transition duration-300 whitespace-nowrap shrink-0 ${
                                activeTab === 'stories' 
                                    ? 'border-yellow-500 text-yellow-500' 
                                    : 'border-transparent text-gray-400 hover:text-gray-300'
                            }`}
                        >
                            🌸 Zahrada příběhů ({stories.length})
                        </button>
                        <button
                            onClick={() => setActiveTab('images')}
                            className={`py-4 px-1 border-b-2 font-medium text-sm transition duration-300 whitespace-nowrap shrink-0 ${
                                activeTab === 'images' 
                                    ? 'border-yellow-500 text-yellow-500' 
                                    : 'border-transparent text-gray-400 hover:text-gray-300'
                            }`}
                        >
                            Nahrávání obrázků
                        </button>
                        <button onClick={() => setActiveTab('messages')} className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${activeTab === 'messages' ? 'border-yellow-500 text-yellow-500' : 'border-transparent text-gray-400'}`}>Zprávy ({inquiries.length})</button>
                        <button
                            onClick={() => setActiveTab('testimonials')}
                            className={`py-4 px-1 border-b-2 font-medium text-sm transition duration-300 whitespace-nowrap shrink-0 ${
                                activeTab === 'testimonials' 
                                    ? 'border-yellow-500 text-yellow-500' 
                                    : 'border-transparent text-gray-400 hover:text-gray-300'
                            }`}
                        >
                            Recenze ({testimonials.length})
                        </button>
                    </div>
                </div>
            </nav>

            {/* Content */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {activeTab === 'messages' && <section><h2 className="text-3xl font-bold mb-8">Zprávy z webu</h2>{inquiries.length === 0 && <p>Zatím nemáte žádné zprávy.</p>}<div className="space-y-4">{[...inquiries].sort((a,b) => (b.timestamp?.toMillis?.() || 0) - (a.timestamp?.toMillis?.() || 0)).map(inquiry => <article className="bg-gray-800 border border-gray-700 rounded-xl p-6" key={inquiry.id}><h3 className="text-xl font-semibold">{inquiry.name}</h3><a href={`mailto:${inquiry.email}`} className="text-yellow-300 break-all">{inquiry.email}</a><p className="text-sm text-gray-400 mt-2">{inquiry.timestamp?.toDate?.().toLocaleString('cs-CZ')}</p><p className="mt-4 whitespace-pre-wrap">{inquiry.message}</p></article>)}</div></section>}
                {activeTab === 'dashboard' && (
                    <div>
                        <h2 className="text-3xl font-bold mb-8">Dashboard</h2>
                        
                        {/* Stats Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                            <div className="bg-gray-800 p-6 rounded-xl">
                                <div className="flex items-center">
                                    <div className="p-3 bg-blue-500 rounded-full">
                                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                        </svg>
                                    </div>
                                    <div className="ml-4">
                                        <h3 className="text-lg font-semibold text-gray-300">Uživatelé</h3>
                                        <p className="text-3xl font-bold text-white">{users.length}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gray-800 p-6 rounded-xl">
                                <div className="flex items-center">
                                    <div className="p-3 bg-green-500 rounded-full">
                                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                        </svg>
                                    </div>
                                    <div className="ml-4">
                                        <h3 className="text-lg font-semibold text-gray-300">Lekce</h3>
                                        <p className="text-3xl font-bold text-white">{lections.length}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gray-800 p-6 rounded-xl">
                                <div className="flex items-center">
                                    <div className="p-3 bg-purple-500 rounded-full">
                                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <div className="ml-4">
                                        <h3 className="text-lg font-semibold text-gray-300">Rezervace</h3>
                                        <p className="text-3xl font-bold text-white">{reservations.length}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gray-800 p-6 rounded-xl">
                                <div className="flex items-center">
                                    <div className="p-3 bg-yellow-500 rounded-full">
                                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                        </svg>
                                    </div>
                                    <div className="ml-4">
                                        <h3 className="text-lg font-semibold text-gray-300">Recenze</h3>
                                        <p className="text-3xl font-bold text-white">{testimonials.length}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Recent Reservations */}
                        <div className="bg-gray-800 rounded-xl p-6 mb-8">
                            <h3 className="text-xl font-bold mb-4">Nejnovější rezervace</h3>
                            <div className="space-y-3">
                                {reservations.slice(0, 5).map((reservation) => (
                                    <div key={reservation.id} className="flex items-center justify-between p-3 bg-gray-700 rounded-lg">
                                        <div>
                                            <p className="font-medium">{reservation.name}</p>
                                            <p className="text-sm text-gray-400">{reservation.email}</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-sm text-gray-400">{formatDate(reservation.createdAt)}</p>
                                            <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                                                reservation.status === 'confirmed' ? 'bg-green-500 text-white' :
                                                reservation.status === 'cancelled' ? 'bg-red-500 text-white' :
                                                'bg-yellow-500 text-black'
                                            }`}>
                                                {reservation.status === 'confirmed' ? 'Potvrzeno' :
                                                 reservation.status === 'cancelled' ? 'Zrušeno' : 'Čeká'}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                                {reservations.length === 0 && (
                                    <div className="text-center py-4">
                                        <p className="text-gray-400">Žádné rezervace</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Upcoming Lections */}
                        <div className="bg-gray-800 rounded-xl p-6">
                            <h3 className="text-xl font-bold mb-4">Nadcházející lekce</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {lections.slice(0, 6).map((lection) => (
                                    <div key={lection.id} className="bg-gray-700 p-4 rounded-lg">
                                        {lection.image ? (
                                            <img 
                                                src={lection.image} 
                                                alt={lection.name}
                                                className="w-full h-32 object-cover rounded-lg mb-3"
                                            />
                                        ) : (
                                            <div className="w-full h-32 bg-gray-600 rounded-lg mb-3 flex items-center justify-center">
                                                <span className="text-gray-400">Bez obrázku</span>
                                            </div>
                                        )}
                                        <h4 className="font-medium text-white mb-2">{lection.name}</h4>
                                        <p className="text-sm text-gray-400 mb-2">{lection.description}</p>
                                        <div className="flex justify-between text-xs text-gray-400">
                                            <span>{lection.date}</span>
                                            <span>{lection.time}</span>
                                        </div>
                                        <div className="mt-2">
                                            <span className="text-xs bg-blue-500 text-white px-2 py-1 rounded-full">
                                                {lection.attendees?.length || 0} účastníků
                                            </span>
                                        </div>
                                    </div>
                                ))}
                                {lections.length === 0 && (
                                    <div className="col-span-full text-center py-4">
                                        <p className="text-gray-400">Žádné lekce</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'lections' && (
                    <div>
                        <h2 className="text-3xl font-bold mb-8">Správa lekcí</h2>
                        
                        {/* Automatic Recurring Lections */}
                        <div className="bg-blue-800 rounded-xl p-6 mb-8">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-xl font-bold mb-2">Automatické opakování lekcí</h3>
                                    <p className="text-gray-300 text-sm">Vytvoří pravidelné lekce na pondělí (90 min, 18:00) a středu (90 min, 18:00)</p>
                                </div>
                                <button
                                    onClick={createRecurringLections}
                                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg transition duration-300"
                                >
                                    Vytvořit na měsíc dopředu
                                </button>
                            </div>
                        </div>

                        {/* Create Lection Form */}
                        <div className="bg-gray-800 rounded-xl p-6 mb-8">
                            <h3 className="text-xl font-bold mb-4">Vytvořit novou lekci</h3>
                            <form onSubmit={handleAddLection} className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-white text-sm font-medium mb-2">
                                            Název lekce
                                        </label>
                                        <input
                                            type="text"
                                            value={lectionName}
                                            onChange={(e) => setLectionName(e.target.value)}
                                            className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500 transition duration-300"
                                            placeholder="Např. Chi Kung pro začátečníky"
                                            required
                                        />
                                    </div>
                                    
                                    <div>
                                        <label className="block text-white text-sm font-medium mb-2">
                                            Obrázek
                                        </label>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => setLectionImageFile(e.target.files[0])}
                                            className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500 transition duration-300"
                                            required
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-white text-sm font-medium mb-2">
                                        Popis lekce
                                    </label>
                                    <textarea
                                        value={lectionDescription}
                                        onChange={(e) => setLectionDescription(e.target.value)}
                                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500 transition duration-300"
                                        rows={4}
                                        placeholder="Popište obsah a cíle lekce..."
                                        required
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-white text-sm font-medium mb-2">
                                            Datum
                                        </label>
                                        <input
                                            type="date"
                                            value={lectionDate}
                                            onChange={(e) => setLectionDate(e.target.value)}
                                            className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500 transition duration-300"
                                            required
                                        />
                                    </div>
                                    
                                    <div>
                                        <label className="block text-white text-sm font-medium mb-2">
                                            Čas
                                        </label>
                                        <input
                                            type="time"
                                            value={lectionTime}
                                            onChange={(e) => setLectionTime(e.target.value)}
                                            className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500 transition duration-300"
                                            required
                                        />
                                    </div>
                                </div>
                                
                                <button
                                    type="submit"
                                    disabled={isLectionUploading}
                                    className="w-full bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-600 text-black font-bold py-3 px-4 rounded-lg transition duration-300"
                                >
                                    {isLectionUploading ? 'Vytváří se...' : 'Vytvořit lekci'}
                                </button>
                            </form>
                        </div>

                        {/* Archive Button */}
                        <div className="mb-8">
                            <button
                                onClick={() => setShowArchivedLections(!showArchivedLections)}
                                className="w-full bg-gray-700 hover:bg-gray-600 text-white font-bold py-4 px-6 rounded-xl transition duration-300 flex items-center justify-center space-x-2"
                            >
                                <i className={`fas ${showArchivedLections ? 'fa-eye-slash' : 'fa-archive'}`}></i>
                                <span>{showArchivedLections ? 'Skrýt archiv' : 'Zobrazit archivované lekce'}</span>
                                {archivedLections.length > 0 && (
                                    <span className="bg-yellow-600 text-black px-3 py-1 rounded-full text-sm font-bold">
                                        {archivedLections.length}
                                    </span>
                                )}
                            </button>
                        </div>

                        {/* Archived Lections */}
                        {showArchivedLections && (
                            <div className="bg-gray-800 rounded-xl p-6 mb-8">
                                <h3 className="text-xl font-bold mb-4 flex items-center">
                                    <i className="fas fa-archive mr-2 text-yellow-500"></i>
                                    Archivované lekce
                                </h3>
                                {archivedLections.length === 0 ? (
                                    <p className="text-gray-400">Žádné archivované lekce</p>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                        {archivedLections.map((lection) => (
                                            <div key={lection.id} className="bg-gray-700 rounded-lg overflow-hidden opacity-75">
                                                {lection.image ? (
                                                    <img 
                                                        src={lection.image} 
                                                        alt={lection.name}
                                                        className="w-full h-40 object-cover grayscale"
                                                    />
                                                ) : (
                                                    <div className="w-full h-40 bg-gray-600 flex items-center justify-center">
                                                        <span className="text-gray-400">Bez obrázku</span>
                                                    </div>
                                                )}
                                                <div className="p-4">
                                                    <h4 className="text-lg font-bold mb-2">{lection.name}</h4>
                                                    <p className="text-gray-400 text-sm mb-3">{lection.description}</p>
                                                    <div className="text-sm text-gray-400 mb-2">
                                                        <i className="fas fa-calendar mr-2"></i>
                                                        {new Date(lection.date).toLocaleDateString('cs-CZ')}
                                                    </div>
                                                    <div className="text-sm text-gray-400 mb-2">
                                                        <i className="fas fa-clock mr-2"></i>
                                                        {lection.time}
                                                    </div>
                                                    <div className="text-sm text-gray-400 mb-3">
                                                        <i className="fas fa-users mr-2"></i>
                                                        {lection.attendees?.length || 0} účastníků
                                                    </div>
                                                    <button
                                                        onClick={() => deleteLection(lection.id)}
                                                        className="w-full bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-lg transition duration-300"
                                                    >
                                                        <i className="fas fa-trash mr-2"></i>
                                                        Smazat z archivu
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Lections List */}
                        <div className="bg-gray-800 rounded-xl p-6">
                            <h3 className="text-xl font-bold mb-4">Všechny lekce</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {lections.map((lection) => (
                                    <div key={lection.id} className="bg-gray-700 rounded-lg overflow-hidden">
                                        {lection.image ? (
                                            <img 
                                                src={lection.image} 
                                                alt={lection.name}
                                                className="w-full h-48 object-cover"
                                            />
                                        ) : (
                                            <div className="w-full h-48 bg-gray-600 flex items-center justify-center">
                                                <span className="text-gray-400">Bez obrázku</span>
                                            </div>
                                        )}
                                        <div className="p-4">
                                            <h4 className="font-bold text-white mb-2">{lection.name}</h4>
                                            <p className="text-sm text-gray-300 mb-3 line-clamp-3">{lection.description}</p>
                                            
                                            <div className="space-y-2 mb-4">
                                                <div className="flex items-center text-sm text-gray-400">
                                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                    </svg>
                                                    {lection.date}
                                                </div>
                                                <div className="flex items-center text-sm text-gray-400">
                                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    {lection.time}
                                                </div>
                                                <div className={`flex items-center text-sm ${(lection.attendees?.length || 0) >= 12 ? 'text-red-400' : 'text-gray-400'}`}>
                                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                                    </svg>
                                                    {Object.keys(lection.attendeesMap || {}).length || lection.attendees?.length || 0}/12 účastníků
                                                    {(Object.keys(lection.attendeesMap || {}).length || lection.attendees?.length || 0) >= 12 && (
                                                        <span className="ml-2 text-red-400 font-bold">PLNÁ</span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Seznam účastníků - NEW MAP-BASED VERSION */}
                                            {((lection.attendeesMap && Object.keys(lection.attendeesMap).length > 0) || (lection.attendees && lection.attendees.length > 0)) && (
                                                <div className="mb-4 p-3 bg-gray-600 rounded-lg">
                                                    <h5 className="text-sm font-medium text-gray-300 mb-2">Účastníci:</h5>
                                                    <div className="text-xs text-gray-400 space-y-1">
                                                        {/* Display attendees from map first (new structure) */}
                                                        {lection.attendeesMap && Object.entries(lection.attendeesMap).map(([attendeeId, attendee]) => (
                                                            <div key={attendeeId} className="flex items-center justify-between truncate">
                                                                <div className="flex items-center">
                                                                    <div className="flex items-center">
                                                                        {attendee.type === 'guest' ? (
                                                                            <div className="flex items-center">
                                                                                <i className="fas fa-user text-yellow-400 mr-1 text-xs"></i>
                                                                                <span className="text-yellow-300">{attendee.name}</span>
                                                                                <span className="ml-1 text-gray-500 text-xs">(host)</span>
                                                                                <span className="ml-1 text-gray-600 text-xs">ID: {attendeeId}</span>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="flex items-center">
                                                                                <i className="fas fa-envelope text-blue-400 mr-1 text-xs"></i>
                                                                                <span>{attendee.email || attendee.name}</span>
                                                                                <span className="ml-1 text-gray-600 text-xs">ID: {attendeeId}</span>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    {attendee.joinedAt && (
                                                                        <span className="ml-2 text-gray-500">
                                                                            ({new Date(attendee.joinedAt.seconds ? attendee.joinedAt.seconds * 1000 : attendee.joinedAt).toLocaleDateString('cs-CZ')})
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                
                                                                {/* Remove button - use attendeeId for guests, name/email for others */}
                                                                <button
                                                                    onClick={() => {
                                                                        const identifier = attendee.type === 'guest' 
                                                                            ? attendeeId  // Use unique ID for guests
                                                                            : (attendee.name || attendee.email); // Use name/email for registered users
                                                                        
                                                                        const displayName = attendee.name || attendee.email || attendeeId;
                                                                        
                                                                        if (window.confirm(`Opravdu chcete odebrat účastníka "${displayName}" z této lekce?`)) {
                                                                            removeAttendee(lection.id, identifier);
                                                                        }
                                                                    }}
                                                                    className="ml-2 px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded transition duration-200 min-w-[24px]"
                                                                    title="Odebrat účastníka"
                                                                    style={{display: 'inline-block'}}
                                                                >
                                                                    ❌
                                                                </button>
                                                            </div>
                                                        ))}
                                                        
                                                        {/* Fallback: Display legacy attendees array (for backward compatibility) */}
                                                        {/* {lection.attendees && lection.attendees.length > 0 && lection.attendees.map((attendee, index) => (
                                                            <div key={`legacy-${index}`} className="flex items-center justify-between truncate">
                                                                <div className="flex items-center">
                                                                    {typeof attendee === 'string' ? (
                                                                        <span>{attendee} <span className="text-gray-600 text-xs">(legacy)</span></span>
                                                                    ) : (
                                                                        <div className="flex items-center">
                                                                            {attendee.type === 'guest' ? (
                                                                                <div className="flex items-center">
                                                                                    <i className="fas fa-user text-yellow-400 mr-1 text-xs"></i>
                                                                                    <span className="text-yellow-300">{attendee.name}</span>
                                                                                    <span className="ml-1 text-gray-500 text-xs">(host-legacy)</span>
                                                                                </div>
                                                                            ) : (
                                                                                <div className="flex items-center">
                                                                                    <i className="fas fa-envelope text-blue-400 mr-1 text-xs"></i>
                                                                                    <span>{attendee.email}</span>
                                                                                    <span className="ml-1 text-gray-600 text-xs">(legacy)</span>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                    {typeof attendee === 'object' && attendee.joinedAt && (
                                                                        <span className="ml-2 text-gray-500">
                                                                            ({new Date(attendee.joinedAt.seconds ? attendee.joinedAt.seconds * 1000 : attendee.joinedAt).toLocaleDateString('cs-CZ')})
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                
                                                                <button
                                                                    onClick={() => {
                                                                        const identifier = typeof attendee === 'string' 
                                                                            ? attendee 
                                                                            : (attendee.name || attendee.email);
                                                                        
                                                                        if (window.confirm(`Opravdu chcete odebrat účastníka "${identifier}" z této lekce?`)) {
                                                                            removeAttendee(lection.id, identifier);
                                                                        }
                                                                    }}
                                                                    className="ml-2 px-1 py-0.5 bg-red-600 hover:bg-red-700 text-white text-xs rounded transition duration-200"
                                                                    title="Odebrat účastníka"
                                                                >
                                                                    <i className="fas fa-times"></i>
                                                                </button>
                                                            </div>
                                                        ))} */}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Přidání účastníka */}
                                            <div className="mb-4 space-y-3">
                                                {/* Přidání registrovaného uživatele */}
                                                <div className="border border-gray-600 rounded-lg p-3">
                                                    <h6 className="text-xs font-medium text-gray-300 mb-2 flex items-center">
                                                        <i className="fas fa-user-check mr-1"></i>
                                                        Registrovaný uživatel
                                                    </h6>
                                                    <div className="space-y-2">
                                                        <input
                                                            type="email"
                                                            placeholder="Email účastníka"
                                                            className="w-full px-2 py-1 bg-gray-600 border border-gray-500 rounded text-white text-xs"
                                                            id={`attendee-${lection.id}`}
                                                        />
                                                        <div className="grid grid-cols-2 gap-2">
                                                            <button
                                                                onClick={() => {
                                                                    const email = document.getElementById(`attendee-${lection.id}`).value;
                                                                    if (email) {
                                                                        addAttendeeToLection(lection.id, email);
                                                                        document.getElementById(`attendee-${lection.id}`).value = '';
                                                                    }
                                                                }}
                                                                className="bg-green-600 hover:bg-green-700 text-white text-xs font-medium py-1 px-2 rounded transition duration-300"
                                                                disabled={(Object.keys(lection.attendeesMap || {}).length || lection.attendees?.length || 0) >= 12}
                                                            >
                                                                Přidat
                                                            </button>
                                                            <button
                                                                onClick={() => {
                                                                    const email = document.getElementById(`attendee-${lection.id}`).value;
                                                                    if (email) {
                                                                        addAttendeeWithPass(lection.id, email);
                                                                        document.getElementById(`attendee-${lection.id}`).value = '';
                                                                    }
                                                                }}
                                                                className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium py-1 px-2 rounded transition duration-300"
                                                                disabled={(Object.keys(lection.attendeesMap || {}).length || lection.attendees?.length || 0) >= 12}
                                                            >
                                                                S permanentkou
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Přidání hosta bez účtu */}
                                                <div className="border border-yellow-600 rounded-lg p-3 bg-yellow-900/10">
                                                    <h6 className="text-xs font-medium text-yellow-300 mb-2 flex items-center">
                                                        <i className="fas fa-user-plus mr-1"></i>
                                                        Host bez účtu
                                                    </h6>
                                                    <div className="space-y-2">
                                                        <input
                                                            type="text"
                                                            placeholder="Jméno a příjmení"
                                                            className="w-full px-2 py-1 bg-gray-600 border border-yellow-500 rounded text-white text-xs"
                                                            id={`guest-${lection.id}`}
                                                        />
                                                        <button
                                                            onClick={async () => {
                                                                const name = document.getElementById(`guest-${lection.id}`).value.trim();
                                                                if (name) {
                                                                    try {
                                                                        await addAttendeeByName(lection.id, name);
                                                                        // Clear input only after successful addition
                                                                        document.getElementById(`guest-${lection.id}`).value = '';
                                                                    } catch (error) {
                                                                        console.error('Error adding guest:', error);
                                                                    }
                                                                } else {
                                                                    alert('Zadejte jméno účastníka');
                                                                }
                                                            }}
                                                            className="w-full bg-yellow-600 hover:bg-yellow-700 text-black text-xs font-medium py-1 px-2 rounded transition duration-300"
                                                            disabled={(Object.keys(lection.attendeesMap || {}).length || lection.attendees?.length || 0) >= 12}
                                                        >
                                                            <i className="fas fa-user-plus mr-1"></i>
                                                            Přidat hosta
                                                        </button>
                                                        <p className="text-xs text-yellow-400/70">
                                                            Pro lidi bez registrovaného účtu
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex space-x-2">
                                                <button
                                                    onClick={() => deleteLection(lection.id)}
                                                    className="flex-1 bg-red-600 hover:bg-red-700 text-white text-sm font-medium py-2 px-4 rounded-lg transition duration-300"
                                                >
                                                    Smazat
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            
                            {lections.length === 0 && (
                                <div className="text-center py-8">
                                    <p className="text-gray-400">Žádné lekce vytvořeny</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}
                {activeTab === 'users' && (
                    <div>
                        <h2 className="text-3xl font-bold mb-8">Správa uživatelů</h2>
                        
                        {/* Create User Form */}
                        <div className="bg-gray-800 rounded-xl p-6 mb-8 border-l-4 border-yellow-500">
                            <div className="flex items-center mb-4">
                                <div className="p-2 bg-yellow-500 rounded-lg mr-3">
                                    <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold">Vytvořit nového klienta</h3>
                                    <p className="text-gray-400 text-sm">Systém automaticky vygeneruje bezpečné heslo</p>
                                </div>
                            </div>
                            
                            <form onSubmit={handleCreateUser} className="space-y-4">
                                <div>
                                    <label className="block text-white text-sm font-medium mb-2">
                                        Email klienta *
                                    </label>
                                    <input
                                        type="email"
                                        value={newUserEmail}
                                        onChange={(e) => setNewUserEmail(e.target.value)}
                                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500 transition duration-300"
                                        placeholder="klient@example.com"
                                        required
                                    />
                                    <p className="text-xs text-gray-400 mt-1">
                                        Klient použije tento email pro přihlášení do panelu
                                    </p>
                                </div>
                                
                                <div className="bg-gray-700 p-4 rounded-lg">
                                    <div className="flex items-center mb-2">
                                        <svg className="w-5 h-5 text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <span className="text-sm font-medium text-white">Co se stane po vytvoření:</span>
                                    </div>
                                    <ul className="text-xs text-gray-300 space-y-1 ml-7">
                                        <li>✓ Automaticky se vygeneruje 8-místné heslo</li>
                                        <li>✓ Klient bude vytvořen v systému</li>
                                        <li>✓ Zobrazí se přihlašovací údaje pro předání klientovi</li>
                                        <li>✓ Klient se přihlásí na <strong>/panel</strong> a změní si heslo</li>
                                    </ul>
                                </div>

                                <button
                                    type="submit"
                                    className="w-full bg-yellow-600 hover:bg-yellow-700 text-black font-bold py-3 px-6 rounded-lg transition duration-300 flex items-center justify-center"
                                >
                                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                    </svg>
                                    Vytvořit klientský účet
                                </button>
                            </form>
                        </div>

                        {/* Password Display Modal - Compact Version */}
                        {showPassword && (
                            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                                <div className="bg-gray-800 p-6 rounded-lg max-w-md w-full mx-4 border border-yellow-500">
                                    <div className="text-center mb-4">
                                        <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
                                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <h3 className="text-xl font-bold text-green-400 mb-1">Účet vytvořen!</h3>
                                    </div>
                                    
                                    <div className="bg-gray-700 p-4 rounded-lg mb-4">
                                        <div className="space-y-3">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-300 mb-1">Email:</label>
                                                <div className="bg-gray-600 p-2 rounded text-center">
                                                    <code className="text-yellow-400 text-sm font-mono">{createdUserEmail}</code>
                                                </div>
                                            </div>
                                            
                                            <div>
                                                <label className="block text-xs font-medium text-gray-300 mb-1">Heslo:</label>
                                                <div className="bg-gray-600 p-2 rounded border border-yellow-500 text-center">
                                                    <code className="text-yellow-400 text-lg font-mono font-bold">{generatedPassword}</code>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-blue-900/50 border border-blue-500 rounded-lg p-3 mb-4">
                                        <p className="text-blue-300 font-medium text-xs mb-2">📋 Instrukce:</p>
                                        <ul className="text-blue-200 text-xs space-y-1">
                                            <li>• Předejte údaje klientovi</li>
                                            <li>• Přihlášení na <strong>/panel</strong></li>
                                            <li>• První přihlášení = změna hesla</li>
                                        </ul>
                                    </div>

                                    <div className="flex space-x-2">
                                        <button
                                            onClick={() => {
                                                navigator.clipboard.writeText(`Panel: https://cestamehovnitrnihoja.cz/panel \n\nEmail: ${createdUserEmail}\nHeslo: ${generatedPassword}`);
                                                alert('Údaje zkopírovány!');
                                            }}
                                            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 px-3 rounded transition duration-300"
                                        >
                                            📋 Kopírovat
                                        </button>
                                        <button
                                            onClick={() => {
                                                setShowPassword(false);
                                                setGeneratedPassword('');
                                                setCreatedUserEmail('');
                                            }}
                                            className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-black text-sm font-bold py-2 px-3 rounded transition duration-300"
                                        >
                                            ✓ OK
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Users Table */}
                        <div className="bg-gray-800 rounded-xl overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-700">
                                    <thead className="bg-gray-700">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                                Email
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                                Permanentka
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                                Budget
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                                Vytvořen
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                                Status
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                                Akce
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-gray-800 divide-y divide-gray-700">
                                        {users.map((userData) => (
                                            <tr key={userData.id} className="hover:bg-gray-700">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm font-medium text-white">
                                                        {userData.email}
                                                    </div>
                                                    <div className="text-sm text-gray-400">
                                                        {userData.role || 'client'}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                                                    <div className="space-y-1">
                                                        <div className="text-yellow-400 font-medium">
                                                            {userData.remainingLections || 0} lekcí
                                                        </div>
                                                        <div className="flex space-x-1">
                                                            <button
                                                                onClick={() => {
                                                                    const amount = prompt('Kolik lekcí přidat?', '11');
                                                                    if (amount) {
                                                                        updateDoc(doc(db, 'users', userData.id), {
                                                                            remainingLections: (userData.remainingLections || 0) + parseInt(amount),
                                                                            updatedAt: new Date()
                                                                        });
                                                                    }
                                                                }}
                                                                className="text-xs bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded"
                                                            >
                                                                +
                                                            </button>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                                                    <div className="space-y-1">
                                                        <div className="text-green-400 font-medium">
                                                            {userData.budgetCredit || 0} Kč
                                                        </div>
                                                        <div className="flex space-x-1">
                                                            <button
                                                                onClick={() => {
                                                                    const amount = prompt('Kolik Kč přidat?', '2000');
                                                                    if (amount) {
                                                                        updateDoc(doc(db, 'users', userData.id), {
                                                                            budgetCredit: (userData.budgetCredit || 0) + parseInt(amount),
                                                                            updatedAt: new Date()
                                                                        });
                                                                    }
                                                                }}
                                                                className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded"
                                                            >
                                                                +
                                                            </button>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                                                    {formatDate(userData.createdAt)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                                        userData.needsPasswordChange 
                                                            ? 'bg-yellow-100 text-yellow-800' 
                                                            : 'bg-green-100 text-green-800'
                                                    }`}>
                                                        {userData.needsPasswordChange ? 'Čeká na změnu hesla' : 'Aktivní'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                    <button
                                                        onClick={() => deleteUser(userData.id)}
                                                        className="text-red-400 hover:text-red-300 transition duration-300"
                                                    >
                                                        Smazat
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            
                            {users.length === 0 && (
                                <div className="text-center py-8">
                                    <p className="text-gray-400">Žádní uživatelé</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'blog' && (
                    <div>
                        <h2 className="text-3xl font-bold mb-8">Správa blog příspěvků</h2>
                        
                        {/* Blog Form */}
                        <div className="bg-gray-800 rounded-xl p-6">
                            <h3 className="text-xl font-bold mb-4">Přidat nový příspěvek</h3>
                            <form onSubmit={handleAddPost} className="space-y-4">
                                <div>
                                    <label className="block text-white text-sm font-medium mb-2">
                                        Název
                                    </label>
                                    <input
                                        type="text"
                                        value={blogTitle}
                                        onChange={(e) => setBlogTitle(e.target.value)}
                                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500 transition duration-300"
                                        required
                                    />
                                </div>
                                
                                <div>
                                    <label className="block text-white text-sm font-medium mb-2">
                                        Shrnutí
                                    </label>
                                    <textarea
                                        value={blogSummary}
                                        onChange={(e) => setBlogSummary(e.target.value)}
                                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500 transition duration-300"
                                        rows={3}
                                        required
                                    />
                                </div>
                                
                                <div>
                                    <label className="block text-white text-sm font-medium mb-2">
                                        Obsah
                                    </label>
                                    <textarea
                                        value={blogContent}
                                        onChange={(e) => setBlogContent(e.target.value)}
                                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500 transition duration-300"
                                        rows={10}
                                        required
                                    />
                                </div>
                                
                                <div>
                                    <label className="block text-white text-sm font-medium mb-2">
                                        Obrázek
                                    </label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => setBlogImageFile(e.target.files[0])}
                                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500 transition duration-300"
                                        required
                                    />
                                </div>
                                
                                <div>
                                    <label className="block text-white text-sm font-medium mb-2">
                                        Datum
                                    </label>
                                    <input
                                        type="date"
                                        value={blogDate}
                                        onChange={(e) => setBlogDate(e.target.value)}
                                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500 transition duration-300"
                                        required
                                    />
                                </div>
                                
                                <button
                                    type="submit"
                                    disabled={isUploading}
                                    className="w-full bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-600 text-black font-bold py-3 px-4 rounded-lg transition duration-300"
                                >
                                    {isUploading ? 'Nahrávání...' : 'Přidat příspěvek'}
                                </button>
                            </form>
                        </div>
                        
                        {/* Blog Management */}
                        <div className="mt-8">
                            <BlogManager 
                                db={db} 
                                auth={auth} 
                                user={user} 
                                appId={appId} 
                            />
                        </div>
                    </div>
                )}

                {activeTab === 'stories' && (
                    <div>
                        <h2 className="text-3xl font-bold mb-8 flex items-center">
                            <span className="text-4xl mr-3">🌸</span>
                            Zahrada příběhů
                        </h2>
                        
                        {/* Story Form */}
                        <div className="bg-gray-800 rounded-xl p-6 mb-8">
                            <h3 className="text-xl font-bold mb-4">Vysadit nový příběh do zahrady</h3>
                            <form onSubmit={handleAddStory} className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-white text-sm font-medium mb-2">
                                            Název příběhu *
                                        </label>
                                        <input
                                            type="text"
                                            value={storyTitle}
                                            onChange={(e) => setStoryTitle(e.target.value)}
                                            className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500 transition duration-300"
                                            placeholder="Např. Moudrý bambus"
                                            required
                                        />
                                    </div>
                                    
                                    <div>
                                        <label className="block text-white text-sm font-medium mb-2">
                                            Autor
                                        </label>
                                        <input
                                            type="text"
                                            value={storyAuthor}
                                            onChange={(e) => setStoryAuthor(e.target.value)}
                                            className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500 transition duration-300"
                                            placeholder="Např. Taoistický příběh"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-white text-sm font-medium mb-2">
                                        Výňatek (krátký úvod)
                                    </label>
                                    <textarea
                                        value={storyExcerpt}
                                        onChange={(e) => setStoryExcerpt(e.target.value)}
                                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500 transition duration-300"
                                        rows={2}
                                        placeholder="Krátký úvod, který se zobrazí na kartě příběhu..."
                                    />
                                </div>

                                <div>
                                    <label className="block text-white text-sm font-medium mb-2">
                                        Obrázek příběhu
                                    </label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => setStoryImageFile(e.target.files[0])}
                                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500 transition duration-300"
                                    />
                                    {storyImageFile && (
                                        <p className="text-sm text-green-400 mt-2">
                                            <i className="fas fa-check-circle mr-2"></i>
                                            Vybraný soubor: {storyImageFile.name}
                                        </p>
                                    )}
                                </div>
                                
                                <div>
                                    <label className="block text-white text-sm font-medium mb-2">
                                        Obsah příběhu *
                                    </label>
                                    <textarea
                                        value={storyContent}
                                        onChange={(e) => setStoryContent(e.target.value)}
                                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500 transition duration-300 font-serif"
                                        rows={15}
                                        placeholder="Příběh se rozvinul jako lotos na vodní hladině..."
                                        required
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-white text-sm font-medium mb-2">
                                            Nálada (emoji)
                                        </label>
                                        <select
                                            value={storyMood}
                                            onChange={(e) => setStoryMood(e.target.value)}
                                            className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500 transition duration-300"
                                        >
                                            <option value="🌿">🌿 Klid</option>
                                            <option value="🌸">🌸 Krása</option>
                                            <option value="🍃">🍃 Lehkost</option>
                                            <option value="🌊">🌊 Hloubka</option>
                                            <option value="🌙">🌙 Moudrost</option>
                                            <option value="☀️">☀️ Radost</option>
                                            <option value="🎋">🎋 Síla</option>
                                            <option value="🪷">🪷 Osvícení</option>
                                            <option value="🏔️">🏔️ Stabilita</option>
                                            <option value="💫">💫 Proměna</option>
                                        </select>
                                    </div>
                                    
                                    <div>
                                        <label className="block text-white text-sm font-medium mb-2">
                                            Kategorie
                                        </label>
                                        <input
                                            type="text"
                                            value={storyCategory}
                                            onChange={(e) => setStoryCategory(e.target.value)}
                                            className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500 transition duration-300"
                                            placeholder="Např. Taoismus, Zen..."
                                        />
                                    </div>
                                    
                                    <div className="flex items-end">
                                        <div className="text-sm text-gray-400 bg-gray-700/50 px-4 py-3 rounded-lg w-full">
                                            <i className="fas fa-info-circle mr-2"></i>
                                            Pole označená * jsou povinná
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-white text-sm font-medium mb-2">
                                        Poznámka (zobrazí se pod příběhem)
                                    </label>
                                    <textarea
                                        value={storyNote}
                                        onChange={(e) => setStoryNote(e.target.value)}
                                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500 transition duration-300 italic"
                                        rows={2}
                                        placeholder="Volitelná poznámka nebo citace na závěr příběhu..."
                                    />
                                </div>
                                
                                <button
                                    type="submit"
                                    disabled={isStoryUploading}
                                    className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white font-bold py-4 px-4 rounded-lg transition duration-300 flex items-center justify-center"
                                >
                                    {isStoryUploading ? (
                                        <>
                                            <i className="fas fa-spinner fa-spin mr-2"></i>
                                            Vysazování příběhu...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-seedling mr-2"></i>
                                            Vysadit příběh do zahrady
                                        </>
                                    )}
                                </button>
                            </form>
                        </div>
                        
                        {/* Stories List */}
                        <div className="bg-gray-800 rounded-xl p-6">
                            <h3 className="text-xl font-bold mb-6">Všechny příběhy v zahradě ({stories.length})</h3>
                            
                            {stories.length === 0 ? (
                                <div className="text-center py-12 text-gray-400">
                                    <span className="text-5xl mb-4 inline-block opacity-50">🍃</span>
                                    <p>Zahrada je zatím prázdná. Vysaďte první příběh.</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {stories.map((story) => (
                                        <div key={story.id} className="bg-gray-700 rounded-lg overflow-hidden relative group hover:bg-gray-600 transition-all duration-300">
                                            {/* Obrázek příběhu */}
                                            {story.image && (
                                                <div className="h-40 overflow-hidden">
                                                    <img 
                                                        src={story.image} 
                                                        alt={story.title}
                                                        className="w-full h-full object-cover"
                                                    />
                                                </div>
                                            )}
                                            
                                            <div className="p-6">
                                                <div className="absolute top-4 right-4 text-3xl opacity-70 z-10">
                                                    {story.mood || '🌿'}
                                                </div>
                                                
                                                <h4 className="text-xl font-bold mb-2 pr-12">{story.title}</h4>
                                                
                                                {story.author && (
                                                    <p className="text-sm text-gray-400 italic mb-3">— {story.author}</p>
                                                )}
                                                
                                                <p className="text-gray-300 mb-4 line-clamp-3">{story.excerpt || story.content?.substring(0, 150) + '...'}</p>
                                            
                                            <div className="flex items-center justify-between text-sm text-gray-400 mb-4">
                                                <span>
                                                    <i className="fas fa-calendar mr-2"></i>
                                                    {story.date}
                                                </span>
                                                <span>
                                                    <i className="fas fa-eye mr-2"></i>
                                                    {story.viewCount || 0} zobrazení
                                                </span>
                                            </div>
                                            
                                            {story.category && (
                                                <span className="inline-block bg-yellow-600/30 text-yellow-400 px-3 py-1 rounded-full text-xs mb-4">
                                                    {story.category}
                                                </span>
                                            )}
                                            
                                                <button
                                                    onClick={() => handleDeleteStory(story.id)}
                                                    className="w-full bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-lg transition duration-300 opacity-0 group-hover:opacity-100"
                                                >
                                                    <i className="fas fa-trash mr-2"></i>
                                                    Odstranit příběh
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'images' && (
                    <div>
                        <ImageUploader user={user} />
                    </div>
                )}

                {activeTab === 'testimonials' && (
                    <div>
                        <h2 className="text-3xl font-bold mb-8">Správa recenzí</h2>
                        
                        {/* Statistiky */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                            <div className="bg-blue-800 p-6 rounded-xl">
                                <div className="text-center">
                                    <h3 className="text-lg font-semibold text-blue-200">Celkem recenzí</h3>
                                    <p className="text-3xl font-bold text-white">{testimonials.length}</p>
                                </div>
                            </div>
                            <div className="bg-green-800 p-6 rounded-xl">
                                <div className="text-center">
                                    <h3 className="text-lg font-semibold text-green-200">Schválené</h3>
                                    <p className="text-3xl font-bold text-white">
                                        {testimonials.filter(t => t.approved === true).length}
                                    </p>
                                </div>
                            </div>
                            <div className="bg-yellow-800 p-6 rounded-xl">
                                <div className="text-center">
                                    <h3 className="text-lg font-semibold text-yellow-200">Čeká na schválení</h3>
                                    <p className="text-3xl font-bold text-white">
                                        {testimonials.filter(t => t.approved !== true).length}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Seznam recenzí */}
                        <div className="bg-gray-800 rounded-xl p-6">
                            <div className="space-y-4">
                                {testimonials.map((testimonial) => (
                                    <div key={testimonial.id} className={`p-6 rounded-lg border-l-4 ${
                                        testimonial.approved === true 
                                            ? 'bg-green-900/30 border-green-500' 
                                            : 'bg-yellow-900/30 border-yellow-500'
                                    }`}>
                                        <div className="flex justify-between items-start">
                                            <div className="flex-1">
                                                {/* Status indikátor */}
                                                <div className="flex items-center mb-3">
                                                    <div className={`w-3 h-3 rounded-full mr-3 ${
                                                        testimonial.approved === true ? 'bg-green-500' : 'bg-yellow-500'
                                                    }`}></div>
                                                    <span className={`text-sm font-medium px-3 py-1 rounded-full ${
                                                        testimonial.approved === true 
                                                            ? 'bg-green-500/20 text-green-300' 
                                                            : 'bg-yellow-500/20 text-yellow-300'
                                                    }`}>
                                                        {testimonial.approved === true ? 'Schváleno' : 'Čeká na schválení'}
                                                    </span>
                                                </div>

                                                {/* Jméno a hodnocení */}
                                                <div className="flex items-center mb-3">
                                                    <h4 className="font-bold text-white text-lg mr-4">{testimonial.name}</h4>
                                                    <div className="flex text-yellow-400">
                                                        {[...Array(5)].map((_, i) => (
                                                            <i key={i} className={`fas fa-star text-sm ${
                                                                i < (testimonial.rating || 5) ? 'text-yellow-400' : 'text-gray-600'
                                                            }`}></i>
                                                        ))}
                                                    </div>
                                                    <span className="ml-2 text-gray-400 text-sm">
                                                        ({testimonial.rating || 5}/5)
                                                    </span>
                                                </div>

                                                {/* Název recenze */}
                                                {testimonial.title && (
                                                    <h5 className="font-semibold text-gray-300 mb-2">{testimonial.title}</h5>
                                                )}

                                                {/* Text recenze */}
                                                <p className="text-gray-300 mb-3 leading-relaxed">"{testimonial.recenze}"</p>
                                                
                                                {/* Časové razítko */}
                                                <p className="text-sm text-gray-400">
                                                    <i className="fas fa-clock mr-1"></i>
                                                    Odesláno: {formatDate(testimonial.timestamp)}
                                                </p>
                                                
                                                {/* Email (pokud je k dispozici) */}
                                                {testimonial.email && (
                                                    <p className="text-sm text-gray-400 mt-1">
                                                        <i className="fas fa-envelope mr-1"></i>
                                                        {testimonial.email}
                                                    </p>
                                                )}
                                            </div>
                                            
                                            {/* Akční tlačítka */}
                                            <div className="flex flex-col space-y-2 ml-4">
                                                {testimonial.approved !== true ? (
                                                    <button
                                                        onClick={async () => {
                                                            try {
                                                                await updateDoc(doc(db, 'testimonials', testimonial.id), {
                                                                    approved: true,
                                                                    approvedAt: new Date(),
                                                                    approvedBy: user.email
                                                                });
                                                                alert('Recenze byla schválena a zobrazí se na webu');
                                                            } catch (error) {
                                                                console.error('Error approving testimonial:', error);
                                                                alert('Chyba při schvalování recenze');
                                                            }
                                                        }}
                                                        className="bg-green-600 hover:bg-green-700 text-white text-sm font-medium py-2 px-4 rounded-lg transition duration-300 flex items-center"
                                                    >
                                                        <i className="fas fa-check mr-1"></i>
                                                        Schválit
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={async () => {
                                                            if (window.confirm('Opravdu chcete zrušit schválení této recenze?')) {
                                                                try {
                                                                    await updateDoc(doc(db, 'testimonials', testimonial.id), {
                                                                        approved: false,
                                                                        unapprovedAt: new Date(),
                                                                        unapprovedBy: user.email
                                                                    });
                                                                    alert('Schválení recenze bylo zrušeno');
                                                                } catch (error) {
                                                                    console.error('Error unapproving testimonial:', error);
                                                                    alert('Chyba při rušení schválení');
                                                                }
                                                            }
                                                        }}
                                                        className="bg-yellow-600 hover:bg-yellow-700 text-black text-sm font-medium py-2 px-4 rounded-lg transition duration-300 flex items-center"
                                                    >
                                                        <i className="fas fa-times mr-1"></i>
                                                        Zrušit schválení
                                                    </button>
                                                )}
                                                
                                                <button
                                                    onClick={async () => {
                                                        if (window.confirm('Opravdu chcete smazat tuto recenzi? Tato akce je nevratná.')) {
                                                            try {
                                                                await deleteDoc(doc(db, 'testimonials', testimonial.id));
                                                                alert('Recenze byla smazána');
                                                            } catch (error) {
                                                                console.error('Error deleting testimonial:', error);
                                                                alert('Chyba při mazání recenze');
                                                            }
                                                        }
                                                    }}
                                                    className="bg-red-600 hover:bg-red-700 text-white text-sm font-medium py-2 px-4 rounded-lg transition duration-300 flex items-center"
                                                >
                                                    <i className="fas fa-trash mr-1"></i>
                                                    Smazat
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                
                                {testimonials.length === 0 && (
                                    <div className="text-center py-12">
                                        <div className="mb-4">
                                            <i className="fas fa-star text-6xl text-gray-600"></i>
                                        </div>
                                        <h3 className="text-xl font-semibold text-gray-400 mb-2">Žádné recenze</h3>
                                        <p className="text-gray-500">Zatím nebyly odeslány žádné recenze od klientů.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default AdminPage;
